// Phase 1 inc 2: the in-memory API's approval execution outcome, as the server records it
// (services/api/src/domain/approvals/executors.ts `DISPOSITIONS`): what became of an approved
// payload, kept apart from the decision. The fake applies what the server applies and reports
// the kinds CompanyOS does not apply as "not applied".
import type { OperationOutput } from "@companyos/sdk";

type Approval = OperationOutput<"approvals.get">;
type Execution = Pick<
  Approval,
  | "execution_state"
  | "execution_failure"
  | "execution_code"
  | "execution_reason"
  | "execution_attempts"
  | "execution_retry_at"
  | "executed_at"
>;

/** Nothing to apply (undecided, rejected, expired). */
export const NO_EXECUTION = {
  execution_state: null,
  execution_failure: null,
  execution_code: null,
  execution_reason: null,
  execution_attempts: 0,
  execution_retry_at: null,
  // B2-GATES: not sent back (a "send back with a note" decision sets it)
  sent_back: false,
  // RECALL-RUN-LIFECYCLE: the fake runs no plan check
  plan_check: null,
  // CORE-CARDS: no escalation card (the fake's decision keeps a card it has)
  card: null,
  chosen_option: null,
  // CORE-PRECEDENTS: no condition filed, no rulings matched
  condition: null,
  precedent_ids: null,
} as const satisfies Omit<Execution, "executed_at"> &
  Pick<
    Approval,
    "sent_back" | "plan_check" | "card" | "chosen_option" | "condition" | "precedent_ids"
  >;

/** The outcome of deciding an approval of `kind` (`state`) at `at`. */
export function executionAfterDecision(
  kind: Approval["kind"],
  state: Approval["state"],
  at: string,
): Execution {
  if (state !== "approved" && state !== "edited") return { ...NO_EXECUTION, executed_at: null };
  const failed = (code: "unsupported" | "own_lifecycle", reason: string): Execution => ({
    ...NO_EXECUTION,
    execution_state: "failed",
    execution_failure: "permanent",
    execution_code: code,
    execution_reason: reason,
    executed_at: null,
  });
  switch (kind) {
    case "page":
      return failed(
        "unsupported",
        "A page edit is not applied by CompanyOS on approval; nothing was changed",
      );
    case "external_action":
      return failed(
        "unsupported",
        "An external action is not applied by CompanyOS on approval; nothing was changed",
      );
    case "delegation":
      return failed(
        "own_lifecycle",
        "A delegation is accepted, declined and completed on its delegation request; approving it here did not accept it",
      );
    case "promotion":
      return {
        ...NO_EXECUTION,
        execution_state: "pending",
        execution_code: "own_lifecycle",
        execution_reason: "Applied when the promotion is accepted in memory review",
        executed_at: null,
      };
    default:
      return {
        ...NO_EXECUTION,
        execution_state: "applied",
        execution_attempts: 1,
        executed_at: at,
      };
  }
}
